type Constructor = Function | Record<string, unknown> | Array<unknown> | string | number | undefined | null;

/**
 * Takes a type `t` and a value `v` and returns `true` if `v` is of type `t`, otherwise returns `false`.
 * A type here can either be a non-case-sensitive string describing a type (e.g. `'object'`) or a type constructor (e.g. `Object`).
 *
 * ```ts
 * tt.is(Boolean, true);
 * // => true
 *
 * tt.is(Number, 3);
 * // => true
 *
 * tt.is(Array, {});
 * // => false
 * ```
 */
export function is(t: 'object' | 'Object' | ObjectConstructor, v: unknown): v is Record<string, any>;
export function is(t: 'function' | 'Function' | FunctionConstructor, v: unknown): v is Function;
export function is(t: 'boolean' | 'Boolean' | BooleanConstructor, v: unknown): v is boolean;
export function is(t: 'number' | 'Number' | NumberConstructor, v: unknown): v is number;
export function is(t: 'array' | 'Array' | ArrayConstructor, v: unknown): v is Array<unknown>;
export function is(t: 'string' | 'String' | StringConstructor, v: unknown): v is string;
export function is(t: 'undefined' | undefined, v: unknown): v is undefined;
export function is(t: 'null' | null, v: unknown): v is null;
export function is(t: 'nan' | number, v: unknown): v is number; // NaN does not have a type
export function is(t: Constructor | string, v: unknown): boolean {
  if (typeof t === 'function') {
    return null != v && v === v && (v as Record<string, any>).constructor === t;
  }
  if (typeof t === 'string') {
    switch (t.toLowerCase()) {
      case 'array':
        return is(Array, v);
      case 'object':
        return is(Object, v);
      case 'boolean':
        return is(Boolean, v);
      case 'number':
        return is(Number, v);
      case 'nan':
        return is(NaN, v);
      case 'string':
        return is(String, v);
      case 'function':
        return is(Function, v);
      case 'null':
        return is(null, v);
      case 'undefined':
        return is(undefined, v);
      default:
        return false;
    }
  }
  if (typeof t === 'number') {
    return v !== v;
  }
  return t === v;
}
